import React from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, Filter, ArrowRight } from 'lucide-react';
import { products, categories } from '../mockData';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const Products = () => {
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get('category');
  
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedCategory, setSelectedCategory] = React.useState(categoryParam || 'all');

  const getTopSellingIds = () => {
    const weekNumber = Math.floor(Date.now() / (7 * 24 * 60 * 60 * 1000));
    const shuffled = [...products].map((p, index) => ({ p, index})).sort((a, b) => 
      {
      const hashA = (a.index * weekNumber * 9301 + 49297) % 280802;
      const hashB = (b.index * weekNumber * 9301 + 49297) % 280802;
      return hashA - hashB;
      })
      .map(({ p }) => p);
    return shuffled.slice(0, 5).map(p => p.id);
  };


  const topSellingIds = getTopSellingIds();

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.composition.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.uses.some(use => use.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'all' || 
                           categories.find(c => c.id === selectedCategory)?.name === product.category;
    
    return matchesSearch && matchesCategory;
  });

  React.useEffect(() => {
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    }
  }, [categoryParam]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Our Products</h1>
          <p className="text-xl text-blue-100 max-w-2xl">
            Comprehensive pharmaceutical solutions for all therapeutic areas
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar - Categories */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-6">
                  <Filter className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-bold text-gray-900">Filter by Category</h3>
                </div>
                <div className="space-y-2">
                  <button
                    onClick={() => { setSelectedCategory('all'); document.getElementById('product-grid')?.scrollIntoView({ behavior: 'smooth' }) }}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-all ${
                      selectedCategory === 'all'
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-medium">All Products</span>
                      <span className={`text-sm ${selectedCategory === 'all' ? 'text-blue-100' : 'text-gray-500'}`}>
                        ({products.length})
                      </span>
                    </div>
                  </button>
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => { setSelectedCategory(category.id); document.getElementById('product-grid')?.scrollIntoView({ behavior: 'smooth' }) }}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-all ${
                        selectedCategory === category.id
                          ? 'bg-blue-600 text-white shadow-md'
                          : 'hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-medium">{category.name}</span>
                        <span className={`text-sm ${selectedCategory === category.id ? 'text-blue-100' : 'text-gray-500'}`}>
                          ({category.count})
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Search Bar */}
            <div className="mb-8">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Search products by name, composition, or usage..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-12 pr-4 py-6 text-lg border-2 focus:border-blue-500 rounded-xl"
                />
              </div>
            </div>

            {/* Results Header */}
            <div id="product-grid" className="mb-6 flex justify-between items-center" style={{ scrollMarginTop: '100px' }}>
              <h2 className="text-2xl font-bold text-gray-900">
                {filteredProducts.length} Product{filteredProducts.length !== 1 ? 's' : ''} Found
              </h2>
            </div>

            {/* Products Grid */}
            {filteredProducts.length > 0 ? (
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredProducts.map((product) => (
                  <Link key={product.id} to={`/products/${product.id}`}>
                    <Card className="h-full hover:shadow-xl transition-all transform hover:-translate-y-2 border-2 hover:border-blue-500 group cursor-pointer bg-white">
                      <CardContent className="p-0">
                        <div className="relative h-48 overflow-hidden rounded-t-lg bg-gradient-to-br from-blue-50 to-white">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-full object-contain bg-white group-hover:scale-110 transition-transform duration-500"
                          />
                          {topSellingIds.includes(product.id) && (
                            <div className="absolute top-4 right-4">
                              <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold" style={{ boxShadow: '0 0 8px 2px rgba(0, 200, 83, 0.6)' }}>
                                Top Selling
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="p-6">
                          <div className="mb-2">
                            <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-semibold">
                              {product.category}
                            </span>
                          </div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                            {product.name}
                          </h3>
                          <p className="text-sm text-gray-500 mb-3 italic">"{product.tagline}"</p>
                          <p className="text-sm text-gray-600 mb-4 line-clamp-2">{product.composition}</p>
                          <div className="flex items-center text-blue-600 font-semibold text-sm group-hover:gap-2 transition-all">
                            View Details <ArrowRight className="ml-1 w-4 h-4" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Search className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">No Products Found</h3>
                <p className="text-gray-600 mb-6">
                  Try adjusting your search or filter criteria
                </p>
                <Button
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedCategory('all');
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Clear Filters
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products;
